
import Vue from 'vue';
import { Component, Emit, Prop, Ref, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const TimelineEmptyState = () =>
	import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue');

import TimeAgo from '@/components/TimeAgo.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import HorizontalTitleListGraphql from '@/components/timeline/HorizontalTitleListGraphql.vue';
import ProviderTimeline from '@/components/timeline/ProviderTimeline.vue';
import TimelineAdUnit from '@/components/timeline/TimelineAdUnit.vue';
import TimelineHeader from '@/components/timeline/TimelineHeader.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { BucketData, FormattedTimelineData } from '@/interfaces/new-title-graphql';

import { TitleEventPayloadGraphql } from '@/helpers/title-helper';

import { NewDateRangeBucket, NewPageType, NewTitlesEdge } from '@/@types/graphql-types';
import { FilterCollection } from '@/stores/filter.store';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

const Filter = namespace('filter');
const User = namespace('user');

@Component({
	name: 'TitleTimelineGraphql',
	components: {
		BasicButton,
		HorizontalTitleListGraphql,
		InfiniteScroll,
		ProviderIcon,
		TimeAgo,
		TimelineAdUnit,
		TimelineEmptyState,
		TimelineHeader,
		ProviderTimeline,
	},
	setup() {
		useProvideClickoutPlacement('title_timeline');
	},
})
export default class TitleTimelineGraphql extends Vue {
	allTitles: NewTitlesEdge[] = [] as NewTitlesEdge[];

	@Prop({ required: true }) formattedTimelineData: FormattedTimelineData[];
	@Prop({ required: false }) isLoading: boolean;
	@Prop({ default: false }) isFirstPage: boolean;
	@Prop({ default: false }) isAtEndOfList: boolean;
	@Prop({ required: true }) newPageType: NewPageType;

	@Filter.Getter('new') filtersNew: FilterCollection;

	@User.Getter isPremium: boolean;

	@Emit('onScrollToTop')
	onScrollToTop() {}

	@Emit('onPosterClick')
	onPosterClick(payload: TitleEventPayloadGraphql) {
		return payload;
	}

	@Emit('mounted')
	onMounted() {}

	@Watch('allNewTitles')
	onAllNewTitles() {
		this.$emit('passUpTitles', this.allNewTitles);
	}

	mounted() {
		this.onMounted();
	}

	get isFetchedListEmpty(): boolean {
		return this.formattedTimelineData.length === 0;
	}

	get showEmptyStateContent(): boolean {
		return !this.isLoading && this.isFetchedListEmpty;
	}

	get isLeavingSoonOrComingSoon() {
		return [NewPageType.LeavingSoon, NewPageType.Upcoming].includes(this.newPageType);
	}

	get infiniteScrollDisabled() {
		return this.isFetchedListEmpty || this.isAtEndOfList;
	}

	get showEmptyTimeline() {
		if (this.isLoading) return false;

		const showEmptyTimeline = this.showEmptyStateContent || this.isAtEndOfList;

		if (this.isLeavingSoonOrComingSoon) {
			return showEmptyTimeline && this.isPremium;
		}
		return showEmptyTimeline;
	}

	get allNewTitles() {
		return this.allTitles.filter(Boolean).flat();
	}

	getIndexByProvider(dayIndex: number, providerIndex: number) {
		if (dayIndex > 0) {
			[...Array(dayIndex).keys()].forEach(i => {
				const item = this.formattedTimelineData[i + 1];
				if (item) {
					providerIndex += item.providers.length;
				}
			});
		}
		return providerIndex;
	}

	getBucketDate(bucket: BucketData) {
		return bucket.key?.date ?? '';
	}

	getBucketType(bucket: FormattedTimelineData) {
		return !bucket.isGroupedByDate ? (bucket.key as NewDateRangeBucket) : null;
	}

	getBucketTranslatable(bucketKey: string) {
		const translationMap: Record<NewDateRangeBucket, string> = {
			[NewDateRangeBucket.Today]: 'WEBAPP_TODAY',
			[NewDateRangeBucket.Tomorrow]: 'WEBAPP_DATE_TOMORROW',
			[NewDateRangeBucket.Next_7Days]: 'WEBAPP_BADGE_IN_DAYS',
			[NewDateRangeBucket.Next_30Days]: 'WEBAPP_BADGE_IN_DAYS',
		};

		const days = bucketKey === NewDateRangeBucket.Next_7Days ? 7 : 30;

		return this.$t(translationMap[bucketKey as NewDateRangeBucket], { days });
	}

	// @ts-ignore the typing is correct for Vue 3 but Vue 2 sees typeof InfiniteScroll as '{}'
	@Ref('infiniteScroll') readonly infiniteScroll?: InstanceType<typeof InfiniteScroll>;

	// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
	resetInfiniteScroll() {
		this.infiniteScroll?.reset();
	}
}
